<template>
  <dashboard-chart-card
    title="Interacciones por tipo de marcación"
    @handleDownloadClick="$emit('handleDownload')"
  >
    <template #info>
      <a-popover
        trigger="hover"
        class="popover"
        placement="bottomRight"
        :overlayStyle="{ width: '250px' }"
      >
        <template #title>
          <span class="titlePopover"> Interacciones por tipo de marcación </span>
        </template>
        <template #content>
          <div class="content">
            Es el número de veces que contesta (outbound) o inicia una llamada (inbound) el contacto
            hacia la empresa. Ejemplo: Si la empresa llama a un contacto y éste responde, se
            contabiliza como una interacción. Si no responde, no se contabiliza. El gráfico se
            visualizará según el rango de fecha y hora que se agregue al filtro.
          </div>
        </template>
        <InfoCircleOutlined class="info-circle" />
      </a-popover>
    </template>

    <div class="section-graph">
      <apexcharts height="250" type="line" :options="chartOptions" :series="series"></apexcharts>

      <div class="title-grid">
        <a-typography-text strong>Total de interacciones</a-typography-text>
        <div class="grid-cards">
          <interaction-card
            v-for="(item, index) in dataSourceInteractions"
            :key="`interaction_${index}`"
            v-bind="item"
          />
        </div>
      </div>
    </div>
  </dashboard-chart-card>
</template>

<script>
import { defineComponent } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import VueApexCharts from "vue3-apexcharts";
import _ from "lodash";
import dayjs from "dayjs";

import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { DialingType } from "@/app/shared/utils/enums";
import DashboardChartCard from "../cards/DashboardChartCard.vue";
import InteractionCard from "@/app/dashboard/components/cards/InteractionCard.vue";

export default defineComponent({
  components: {
    DashboardChartCard,
    InfoCircleOutlined,
    apexcharts: VueApexCharts,
    InteractionCard,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    hourRange: {
      required: true,
      type: Object,
    },
  },
  computed: {
    rawInteractionsData() {
      const interactions = this.data?.interactions;
      const hourRangeArray = Object.keys(this.hourRange).map((hour) => Number(hour));

      if (hourRangeArray.length !== 0) {
        const formattedInteractionsData = hourRangeArray.flatMap((hour) => [
          { hour, count: 0, type_dialing: DialingType.Inbound },
          { hour, count: 0, type_dialing: DialingType.Manual },
          { hour, count: 0, type_dialing: DialingType.Predictive },
          { hour, count: 0, type_dialing: DialingType.Progressive },
        ]);

        interactions?.forEach((interaction) => {
          const interactionHour = dayjs(interaction?.date).hour();

          const idx = formattedInteractionsData.findIndex(
            ({ hour, type_dialing }) =>
              interactionHour === hour && interaction?.type_dialing === type_dialing
          );

          const { type_dialing, count } = interaction;
          const newInteraction = { hour: interactionHour, type_dialing, count };

          formattedInteractionsData[idx] = newInteraction;
        });

        return formattedInteractionsData;
      }

      return interactions;
    },
    dataSourceInteractions() {
      const data = [
        {
          title: "Predictivo",
          color: "blue",
          number: this.predictiveData,
        },
        {
          title: "Progresivo",
          color: "cyan",
          number: this.progressiveData,
        },
        {
          title: "Manual",
          color: "orange",
          number: this.manualData,
        },
        {
          title: "M. entrante",
          color: "lime",
          number: this.entryData,
        },
      ];
      return data;
    },
    progressiveData() {
      const pro = this.data?.type_dialing.find((x) => x.type_dialing == DialingType.Progressive);
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    predictiveData() {
      const pro = this.data?.type_dialing.find((x) => x.type_dialing == DialingType.Predictive);
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    manualData() {
      const pro = this.data?.type_dialing.find((x) => x.type_dialing == DialingType.Manual);
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    entryData() {
      const pro = this.data?.type_dialing.find((x) => x.type_dialing == DialingType.Inbound);
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    seriePredictive() {
      const array = this.rawInteractionsData?.filter(
        (val) => val.type_dialing == DialingType.Predictive
      );
      const serie = [];
      this.interactionsCategories.forEach((item) => {
        const obj = array.find((x) => (x.date ? x.date == item : x.hour == item));
        if (obj) serie.push(obj.count);
        else serie.push(0);
      });
      return serie;
    },
    serieProgressive() {
      const array = this.rawInteractionsData?.filter(
        (val) => val.type_dialing == DialingType.Progressive
      );
      const serie = [];
      this.interactionsCategories.forEach((item) => {
        const obj = array.find((x) => (x.date ? x.date == item : x.hour == item));
        if (obj) serie.push(obj.count);
        else serie.push(0);
      });
      return serie;
    },
    serieManual() {
      const array = this.rawInteractionsData?.filter(
        (val) => val.type_dialing == DialingType.Manual
      );
      const serie = [];
      this.interactionsCategories.forEach((item) => {
        const obj = array.find((x) => (x.date ? x.date == item : x.hour == item));
        if (obj) serie.push(obj.count);
        else serie.push(0);
      });
      return serie;
    },
    serieEntry() {
      const array = this.rawInteractionsData?.filter(
        (val) => val.type_dialing == DialingType.Inbound
      );
      const serie = [];
      this.interactionsCategories.forEach((item) => {
        const obj = array.find((x) => (x.date ? x.date == item : x.hour == item));
        if (obj) serie.push(obj.count);
        else serie.push(0);
      });
      return serie;
    },
    series() {
      return [
        {
          name: "Predictivo",
          data: this.seriePredictive,
        },
        {
          name: "Progresivo",
          data: this.serieProgressive,
        },
        {
          name: "Manual",
          data: this.serieManual,
        },
        {
          name: "Marcacion Entrante",
          data: this.serieEntry,
        },
      ];
    },
    interactions() {
      return _.sortBy(this.rawInteractionsData, ["date", "hour", "count"]);
    },
    interactionsCategories() {
      return _.sortedUniq(this.interactions.map((value) => (value.date ? value.date : value.hour)));
    },
    interactionsCategoriesLabel() {
      return _.sortedUniq(
        this.interactions.map((value) =>
          value.date ? dateTimeToSlashFormat(value.date, false) : value.hour + ":00hrs"
        )
      );
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          height: 250,
          zoom: {
            autoScaleYaxis: true,
          },
          toolbar: {
            show: false,
          },
        },
        stroke: {
          width: 2,
          curve: "straight",
        },
        colors: ["#91D5FF", "#87E8DE", "#FFD591", "#EAFF8F"],
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
          style: "hollow",
        },
        xaxis: {
          categories: this.interactionsCategoriesLabel,
        },
        tooltip: {
          x: {
            format: "dd MM",
          },
          y: {
            formatter: (val) => val,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      };
    },
  },
});
</script>

<style lang="sass" scoped>
.popover
  margin-right: 20px
.info-circle
  font-size: 14px
  color: #51555A
.content
  font-weight: 300
  color: $gray-8
.card-div
  width: 9vw
  height: 100%
  background: #FAFAFA
  border-radius: 8px
  text-align: center
  display: flex
  flex-direction: column
  justify-content: space-between
  color: #262626
  font-size: 14px
  line-height: 20px
  & > div
    margin-top: 10px
  & > p
    margin-top: 10px
    font-size: 18px
    color: #595959

  &__large
    width: 100%
    grid-column: 1/3
.color
  border-radius: 0px 0px 8px 8px
  width: 100%
  height: 10px
  &__pred
    background: #91D5FF
  &__progre
    background: #87E8DE
  &__manual
    background: #FFD591
  &__entry
    background: #EAFF8F
.section-graph
  display: grid
  grid-template-columns: 1.6fr 0.4fr
  gap: 8px
.grid-cards
  display: grid
  grid-template-columns: 1fr 1fr
  grid-template-rows: 1fr 1fr
  gap: 8px
  height: 80%
.title-grid
  text-align: center
  height: 95%
  & > div
    padding: 10px

.titlePopover
  font-size: 16px
  font-weight: 600
</style>
